import { c as _c } from "react/compiler-runtime";
import { formatCurrency, getPriceDisplay, getPriceDisplayRent } from "@/lib/price";
import { Tooltip, TooltipContent, TooltipTrigger } from "../ui/tooltip";
import AuctionTimer from "../pages/search/AuctionTimer";
import { Clock } from "lucide-react";
import { cn } from "@/lib/utils";
import AuctionPriceTable from "./AuctionPriceTable";
import { BusinessType } from "@/types/real-estate";
import { useTranslations } from "next-intl";
type Props = {
  businessType?: BusinessType;
  rentPrice?: string | null;
  salePrice?: string | null;
  minPrice?: string | null;
  lastBidPrice?: string | null;
  openingPrice?: string | null;
  auction_finish_time?: string | null;
  showTooltip?: boolean;
  className?: string;
  popoverSide?: "top" | "right" | "bottom" | "left";
  popoverAlign?: "start" | "center" | "end";
  groupPrice?: {
    minPrice: string;
    maxPrice: string;
  } | null;
  compact?: boolean;
};
export default function Price(t0) {
  const $ = _c(60);
  const {
    businessType,
    salePrice,
    rentPrice,
    lastBidPrice,
    openingPrice,
    minPrice,
    auction_finish_time,
    showTooltip: t1,
    popoverSide: t2,
    popoverAlign: t3,
    groupPrice,
    className,
    compact: t4
  } = t0;
  const showTooltip = t1 === undefined ? false : t1;
  const popoverSide = t2 === undefined ? "right" : t2;
  const popoverAlign = t3 === undefined ? "center" : t3;
  const compact = t4 === undefined ? false : t4;
  const t = useTranslations("components.price");
  if (businessType === BusinessType.Rent || businessType === BusinessType.RoomRent) {
    if (groupPrice) {
      const isSamePrice = groupPrice.minPrice === groupPrice.maxPrice;
      let t5;
      if ($[0] !== className) {
        t5 = cn("font-semibold text-sm sm:leading-7", className);
        $[0] = className;
        $[1] = t5;
      } else {
        t5 = $[1];
      }
      let t6;
      if ($[2] !== groupPrice.maxPrice || $[3] !== groupPrice.minPrice || $[4] !== isSamePrice) {
        t6 = isSamePrice ? getPriceDisplayRent({
          rentPrice: groupPrice.minPrice
        }).price : `${getPriceDisplayRent({
          rentPrice: groupPrice.minPrice,
          currency: false
        }).price} - ${getPriceDisplayRent({
          rentPrice: groupPrice.maxPrice
        }).price}`;
        $[2] = groupPrice.maxPrice;
        $[3] = groupPrice.minPrice;
        $[4] = isSamePrice;
        $[5] = t6;
      } else {
        t6 = $[5];
      }
      let t7;
      if ($[6] !== t5 || $[7] !== t6) {
        t7 = <p className={t5}>{t6}</p>;
        $[6] = t5;
        $[7] = t6;
        $[8] = t7;
      } else {
        t7 = $[8];
      }
      return t7;
    }
    let t5;
    if ($[9] !== className) {
      t5 = cn("font-semibold text-sm leading-7", className);
      $[9] = className;
      $[10] = t5;
    } else {
      t5 = $[10];
    }
    let t6;
    if ($[11] !== rentPrice || $[12] !== t) {
      t6 = rentPrice ? getPriceDisplayRent({
        rentPrice
      }).price : t("onRequest");
      $[11] = rentPrice;
      $[12] = t;
      $[13] = t6;
    } else {
      t6 = $[13];
    }
    let t7;
    if ($[14] !== t5 || $[15] !== t6) {
      t7 = <p className={t5}>{t6}</p>;
      $[14] = t5;
      $[15] = t6;
      $[16] = t7;
    } else {
      t7 = $[16];
    }
    return t7;
  }
  let price;
  let t5;
  if ($[17] !== businessType || $[18] !== compact || $[19] !== lastBidPrice || $[20] !== minPrice || $[21] !== salePrice || $[22] !== t) {
    price = getPriceDisplay({
      businessType,
      salePrice,
      lastBidPrice,
      minPrice
    });
    t5 = price.value ? compact ? formatCurrency(price.value) : price.price : price.auctionPrice ? price.price : t("onRequest");
    $[17] = businessType;
    $[18] = compact;
    $[19] = lastBidPrice;
    $[20] = minPrice;
    $[21] = salePrice;
    $[22] = t;
    $[23] = price;
    $[24] = t5;
  } else {
    price = $[23];
    t5 = $[24];
  }
  const displayPrice = t5;
  if (!price.auctionPrice) {
    let t6;
    if ($[25] !== className) {
      t6 = cn("font-semibold text-sm! sm:text-xl leading-7 whitespace-nowrap", className);
      $[25] = className;
      $[26] = t6;
    } else {
      t6 = $[26];
    }
    let t7;
    if ($[27] !== displayPrice || $[28] !== t6) {
      t7 = <p className={t6}>{displayPrice}</p>;
      $[27] = displayPrice;
      $[28] = t6;
      $[29] = t7;
    } else {
      t7 = $[29];
    }
    return t7;
  }
  if (!showTooltip) {
    let t6;
    if ($[30] !== className) {
      t6 = cn("font-semibold text-sm! sm:text-xl leading-7", className);
      $[30] = className;
      $[31] = t6;
    } else {
      t6 = $[31];
    }
    let t7;
    if ($[32] !== displayPrice || $[33] !== t6) {
      t7 = <p className={t6}>{displayPrice}</p>;
      $[32] = displayPrice;
      $[33] = t6;
      $[34] = t7;
    } else {
      t7 = $[34];
    }
    return t7;
  }
  let t6;
  if ($[35] !== className) {
    t6 = cn("cursor-pointer font-semibold text-sm! sm:text-xl leading-7", className);
    $[35] = className;
    $[36] = t6;
  } else {
    t6 = $[36];
  }
  let t7;
  if ($[37] !== displayPrice || $[38] !== t6) {
    t7 = <TooltipTrigger asChild={true} data-sentry-element="TooltipTrigger" data-sentry-source-file="Price.tsx"><p className={t6}>{displayPrice}</p></TooltipTrigger>;
    $[37] = displayPrice;
    $[38] = t6;
    $[39] = t7;
  } else {
    t7 = $[39];
  }
  let t8;
  if ($[40] !== t) {
    t8 = t("auction");
    $[40] = t;
    $[41] = t8;
  } else {
    t8 = $[41];
  }
  let t9;
  if ($[42] !== t8) {
    t9 = <span className="text-zinc-800 font-semibold text-lg">{t8}</span>;
    $[42] = t8;
    $[43] = t9;
  } else {
    t9 = $[43];
  }
  const t10 = salePrice ?? "";
  const t11 = lastBidPrice ?? "";
  const t12 = minPrice ?? "";
  const t13 = openingPrice ?? "";
  let t14;
  if ($[44] !== t10 || $[45] !== t11 || $[46] !== t12 || $[47] !== t13) {
    t14 = <AuctionPriceTable salePrice={t10} lastBidPrice={t11} minPrice={t12} openingPrice={t13} data-sentry-element="AuctionPriceTable" data-sentry-source-file="Price.tsx" />;
    $[44] = t10;
    $[45] = t11;
    $[46] = t12;
    $[47] = t13;
    $[48] = t14;
  } else {
    t14 = $[48];
  }
  let t15;
  if ($[49] !== auction_finish_time) {
    t15 = auction_finish_time && <div className="text-primary text-sm font-medium flex items-center justify-between"><Clock className="h-4 w-4 mr-1.5" /><AuctionTimer auctionFinishTime={auction_finish_time} showSeconds={true} /></div>;
    $[49] = auction_finish_time;
    $[50] = t15;
  } else {
    t15 = $[50];
  }
  let t16;
  if ($[51] !== popoverAlign || $[52] !== popoverSide || $[53] !== t14 || $[54] !== t15 || $[55] !== t9) {
    t16 = <TooltipContent side={popoverSide} align={popoverAlign} sideOffset={10} className="flex bg-white flex-col shadow-2xl rounded-2xl gap-4 text-sm text-zinc-800 px-6 py-4 min-w-72" data-sentry-element="TooltipContent" data-sentry-source-file="Price.tsx">{t9}{t14}{t15}</TooltipContent>;
    $[51] = popoverAlign;
    $[52] = popoverSide;
    $[53] = t14;
    $[54] = t15;
    $[55] = t9;
    $[56] = t16;
  } else {
    t16 = $[56];
  }
  let t17;
  if ($[57] !== t16 || $[58] !== t7) {
    t17 = <Tooltip data-sentry-element="Tooltip" data-sentry-component="Price" data-sentry-source-file="Price.tsx">{t7}{t16}</Tooltip>;
    $[57] = t16;
    $[58] = t7;
    $[59] = t17;
  } else {
    t17 = $[59];
  }
  return t17;
}